import React, { useEffect, useState } from "react";
import { api, resolveMedia } from "@/lib/api";
import { Handshake, ArrowUpRight } from "@phosphor-icons/react";

export function SponsorBanner() {
  const [sponsors, setSponsors] = useState([]);
  const [idx, setIdx] = useState(0);

  useEffect(() => {
    api.get("/sponsors").then((r) => setSponsors(r.data || [])).catch(() => {});
  }, []);

  useEffect(() => {
    if (sponsors.length <= 1) return;
    const t = setInterval(() => setIdx((i) => (i + 1) % sponsors.length), 5000);
    return () => clearInterval(t);
  }, [sponsors.length]);

  useEffect(() => {
    if (idx >= sponsors.length) setIdx(0);
  }, [sponsors.length, idx]);

  return (
    <section className="relative border-y border-white/10 bg-[#0A0A0A]" data-testid="sponsor-banner">
      <div className="max-w-7xl mx-auto px-6 py-14">
        <div className="flex items-center gap-3 mb-8">
          <Handshake size={26} weight="fill" className="text-[#FF5722]" />
          <div>
            <p className="text-[10px] tracking-[0.3em] uppercase text-[#FF5722] font-semibold">Proudly supported by</p>
            <h2 className="font-display text-3xl sm:text-4xl uppercase leading-none">Our Sponsors</h2>
          </div>
        </div>

        {sponsors.length === 0 ? (
          <a
            href="/register"
            data-testid="sponsor-placeholder"
            className="group flex flex-col items-center justify-center gap-3 border-2 border-dashed border-white/15 hover:border-[#FF5722] py-16 px-6 text-center transition-colors duration-200"
          >
            <Handshake size={40} className="text-white/30 group-hover:text-[#FF5722] transition-colors" />
            <span className="font-display text-2xl sm:text-3xl uppercase text-white/70 group-hover:text-white transition-colors">Sponsor Your Business Here</span>
            <span className="text-sm text-white/40">Reach the whole league — become an IEFSC sponsor</span>
          </a>
        ) : (
          <div className="relative">
            <div className="relative overflow-hidden">
              <div
                className="flex transition-transform duration-700 ease-out"
                style={{ transform: `translateX(-${idx * 100}%)` }}
                data-testid="sponsor-track"
              >
                {sponsors.map((s, i) => (
                  <a
                    key={s.id}
                    href={s.website_url || "#"}
                    target={s.website_url ? "_blank" : undefined}
                    rel="noopener noreferrer"
                    data-testid={`sponsor-slide-${i}`}
                    className="group relative w-full shrink-0 flex flex-col items-center justify-center gap-4 bg-[#121212] border border-white/10 hover:border-[#FF5722] transition-colors duration-200 py-10 px-6"
                  >
                    {s.image_url ? (
                      <img
                        src={resolveMedia(s.image_url)}
                        alt={s.name}
                        className="max-h-32 sm:max-h-40 w-auto max-w-full object-contain"
                        data-testid={`sponsor-image-${i}`}
                      />
                    ) : (
                      <span className="font-display text-3xl sm:text-4xl uppercase text-white">{s.name}</span>
                    )}
                    <span className="flex items-center gap-1.5 text-sm uppercase tracking-wide text-white/60 group-hover:text-[#FF5722] transition-colors">
                      {s.name}
                      {s.website_url && <ArrowUpRight size={16} weight="bold" />}
                    </span>
                  </a>
                ))}
              </div>
            </div>

            {sponsors.length > 1 && (
              <div className="flex items-center justify-center gap-2 mt-5" data-testid="sponsor-dots">
                {sponsors.map((s, i) => (
                  <button
                    key={s.id}
                    onClick={() => setIdx(i)}
                    aria-label={`Show sponsor ${i + 1}`}
                    data-testid={`sponsor-dot-${i}`}
                    className={`h-2 transition-all duration-300 ${i === idx ? "w-8 bg-[#FF5722]" : "w-2 bg-white/25 hover:bg-white/50"}`}
                  />
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </section>
  );
}
